import { nanoid } from 'nanoid'
import { Capacitor } from '@capacitor/core'
import { isMobile } from '../utils'
import { getSignedURL } from '../picture/get-signed-url'
import { Attachment } from '../types'
import { downloadAction, shareAction, cacheURL } from '../core'

export function generateAttachmentId (): string {
  return nanoid()
}

export async function getData (attachment: Attachment): Promise<string> {
  if (attachment.embed) {
    return attachment.embed
  } else if (attachment.url) {
    if (attachment.url.indexOf('s3:') == 0) {
      const result = await getSignedURL(attachment.url)
      return result.signedURL as string
    } else {
      return attachment.url
    }
  } else if (attachment.thumbnail) {
    return attachment.thumbnail
  } else if (attachment.file) {
    return URL.createObjectURL(attachment.file)
  } else {
    return ''
  }
}

export function canDownload (): boolean {
  return !Capacitor.isNativePlatform() || !isMobile.apple.device
}

export function canShare (): boolean {
  return !!Capacitor.isNativePlatform()
}

export async function downloadAttachment (attachment: Attachment) {
  let url: string = attachment.url ?? ''
  let filename = attachment.title ?? ''
  if (url.startsWith('s3:')) {
    const result = await getSignedFileURL(attachment)
    url = result.signedURL
    if (result.filename) {
      filename = result.filename
    }
  } else if (!url && attachment.file) {
    url = URL.createObjectURL(attachment.file)
  }
  downloadAction(url, filename, attachment.type || '')
}

export async function shareAttachment (attachment: Attachment) {
  const cached = await getCachedFile(attachment)
  shareAction(cached.path, attachment.type || '')
}

export function getSignedFileURL (attachment: Attachment) {
  if (!attachment) {
    throw Error('no attachment')
  }
  const url = attachment.url
  if (!url) {
    throw Error('empty attachment url')
  }
  return getSignedURL(url, attachment.title)
}

export async function getCachedFile (attachment: Attachment, progressCb?: (progress: string) => void) {
  if (!attachment) {
    throw Error('no attachment')
  }
  const size = attachment.size ?? 1024 ^ 3
  const progress = (value: number) => {
    if (progressCb) {
      progressCb(Math.floor((value * 100) / size) + '%')
    }
  }
  let url: string = attachment.url ?? ''
  let filename = attachment.title ?? ''
  if (url?.startsWith('s3:')) {
    const result = await getSignedFileURL(attachment)
    url = result.signedURL
    if (result.filename) {
      filename = result.filename
    }
  } else if (!url && attachment.file) {
    url = URL.createObjectURL(attachment.file)
  }
  return cacheURL(url, filename ?? '', attachment.type || '', progress)
}
